import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Plus, Trash2, Edit, ExternalLink, Newspaper, Save, X, 
  Eye, EyeOff, Calendar, Link, FileText, Upload, Image, Loader2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const FraudNewsAdmin = ({ token }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    source: '',
    url: '',
    summary: '',
    summary_en: '',
    published_date: '',
    image_url: ''
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/fraud-news?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.source || !formData.url) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      if (selectedArticle) {
        await axios.put(`${API}/fraud-news/${selectedArticle.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/fraud-news`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article ajouté');
      }
      setShowDialog(false);
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    if (!selectedArticle) return;
    
    try {
      await axios.delete(`${API}/fraud-news/${selectedArticle.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article supprimé');
      setShowDeleteDialog(false);
      setSelectedArticle(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (article) => {
    try {
      await axios.put(`${API}/fraud-news/${article.id}`, {
        is_active: !article.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(article.is_active ? 'Article masqué' : 'Article affiché');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_en: '',
      source: '',
      url: '',
      summary: '',
      summary_en: '',
      published_date: '',
      image_url: ''
    });
    setSelectedArticle(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    try {
      setUploading(true);
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/fraud-news/upload-image`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        toast.success('Image téléchargée avec succès');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement de l\'image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openEditDialog = (article) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title || '',
      title_en: article.title_en || '',
      source: article.source || '',
      url: article.url || '',
      summary: article.summary || '',
      summary_en: article.summary_en || '',
      published_date: article.published_date || '',
      image_url: article.image_url || ''
    });
    setShowDialog(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-brand-600" />
          Articles de presse sur les fraudes
        </CardTitle>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="bg-brand-600 hover:bg-brand-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un article
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Chargement...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Newspaper className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun article de presse</p>
            <p className="text-sm">Ajoutez des articles de journaux parlant de fraudes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <div 
                key={article.id} 
                className={`border rounded-lg p-4 ${article.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{article.title}</h4>
                      {!article.is_active && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Masqué</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      <span className="font-medium">{article.source}</span>
                      {article.published_date && (
                        <span className="ml-2">• {article.published_date}</span>
                      )}
                    </div>
                    {article.summary && (
                      <p className="text-sm text-gray-600 mb-2">{article.summary}</p>
                    )}
                    <a 
                      href={article.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Voir l'article
                    </a>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(article)}
                      title={article.is_active ? 'Masquer' : 'Afficher'}
                    >
                      {article.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(article)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => { setSelectedArticle(article); setShowDeleteDialog(true); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
          <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Newspaper className="h-5 w-5 text-brand-600" />
                {selectedArticle ? 'Modifier l\'article' : 'Ajouter un article'}
              </AlertDialogTitle>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Titre (français) *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: Un homme arrêté pour fraude de billets"
                />
              </div>

              <div className="space-y-2">
                <Label>Titre (anglais)</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                  placeholder="Ex: Man arrested for ticket fraud"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source / Journal *</Label>
                  <Input
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                    placeholder="Ex: La Presse, TVA Nouvelles"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de publication</Label>
                  <Input
                    type="date"
                    value={formData.published_date}
                    onChange={(e) => setFormData({...formData, published_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL de l'article *</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Résumé (français)</Label>
                <Textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({...formData, summary: e.target.value})}
                  placeholder="Bref résumé de l'article..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Résumé (anglais)</Label>
                <Textarea
                  value={formData.summary_en}
                  onChange={(e) => setFormData({...formData, summary_en: e.target.value})}
                  placeholder="Brief summary..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Image de l'article</Label>
                <div className="space-y-3">
                  {/* Image Preview */}
                  {formData.image_url && (
                    <div className="relative w-full h-32 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={formData.image_url.startsWith('/api') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url}
                        alt="Aperçu"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-1"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Téléchargement...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {formData.image_url ? 'Changer l\'image' : 'Télécharger une image'}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Or use URL */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="text-xs text-gray-400">ou</span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>
                  
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="URL de l'image (https://...)"
                  />
                </div>
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel onClick={resetForm}>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleSubmit}
                className="bg-brand-600 hover:bg-brand-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {selectedArticle ? 'Enregistrer' : 'Ajouter'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l'article?</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer l'article "{selectedArticle?.title}"? 
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default FraudNewsAdmin;
